/**
 * Herkese açık popup'ların ortak tipleri ve yardımcıları. Hem istemci
 * (`PublicPopups`) hem sunucu (`SitewideTopbar`) aynı kapatma anahtarını ve
 * renk süzgecini kullanır; iki taraf ayrı yazılırsa kapatılan şerit geri gelir.
 */

export type PopupType = "topbar" | "sidebar_top" | "sidebar_center" | "sidebar_bottom";

export type PublicPopup = {
  id: number;
  type: PopupType;
  title: string;
  content: string | null;
  target_paths?: string[];
  image: string | null;
  alt: string | null;
  background_color: string | null;
  text_color: string | null;
  button_text: string | null;
  button_color: string | null;
  button_hover_color: string | null;
  button_text_color: string | null;
  link_url: string | null;
  link_target: "_self" | "_blank" | string;
  text_behavior: "static" | "marquee" | string;
  scroll_speed?: number | null;
  closeable: boolean;
  delay_seconds: number;
  display_frequency: "always" | "once" | "daily" | string;
  order: number;
};

/**
 * İçerikte bu belirteç varsa şerit metin yerine canlı fiyat ortalamalarını
 * gösterir (haller arası ortalama, `/prices/featured-list`). Admin panelinde
 * içerik alanına yazılır; metnin geri kalanı yok sayılır.
 */
export const PRICE_STRIP_TOKEN = "{{fiyat_seridi}}";

export function hasPriceStrip(popup: Pick<PublicPopup, "content">): boolean {
  return (popup.content ?? "").includes(PRICE_STRIP_TOKEN);
}

export function popupStorageKey(id: number): string {
  return `hal-popup:${id}`;
}

export function popupDomId(id: number): string {
  return `hh-popup-${id}`;
}

function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

export function isPopupDismissed(popup: Pick<PublicPopup, "id" | "display_frequency">): boolean {
  if (typeof window === "undefined") return false;
  try {
    const key = popupStorageKey(popup.id);
    if (popup.display_frequency === "once") return window.localStorage.getItem(key) === "closed";
    if (popup.display_frequency === "daily") return window.localStorage.getItem(key) === todayKey();
    return window.sessionStorage.getItem(key) === "closed";
  } catch {
    return false;
  }
}

export function markPopupDismissed(popup: Pick<PublicPopup, "id" | "display_frequency">): void {
  if (typeof window === "undefined") return;
  try {
    const key = popupStorageKey(popup.id);
    if (popup.display_frequency === "once") window.localStorage.setItem(key, "closed");
    else if (popup.display_frequency === "daily") window.localStorage.setItem(key, todayKey());
    else window.sessionStorage.setItem(key, "closed");
  } catch {
    // depolama kapalıysa şerit bu sayfada kapanır, sonraki sayfada yeniden görünür
  }
}

/**
 * Sunucuda basılan şeridin ilk boyamadan ÖNCE gizlenmesi için satır içi betik.
 * Hidrasyondan sonra gizlemek sayfayı yukarı kaydırır (CLS); betik öğenin hemen
 * ardından çalıştığı için kapatılmış şerit hiç görünmez. Mantık
 * `isPopupDismissed` ile aynı olmalı.
 */
export function dismissedCheckScript(popup: Pick<PublicPopup, "id" | "display_frequency">): string {
  const key = JSON.stringify(popupStorageKey(popup.id));
  const domId = JSON.stringify(popupDomId(popup.id));
  const freq = JSON.stringify(String(popup.display_frequency));
  return `(function(){try{var f=${freq},k=${key},v=f==="always"?sessionStorage.getItem(k):localStorage.getItem(k);`
    + `if(f==="daily"?v===new Date().toISOString().slice(0,10):v==="closed"){var e=document.getElementById(${domId});if(e)e.hidden=true}}catch(_){}})()`;
}

export function sanitizeColor(value: string | null | undefined): string | undefined {
  const raw = String(value ?? "").trim();
  if (!raw) return undefined;
  if (/^#[0-9a-f]{3,8}$/i.test(raw)) return raw;
  if (/^(rgb|rgba|hsl|hsla)\([\d\s,.%/-]+\)$/i.test(raw)) return raw;
  return undefined;
}

/** Sunucuda basılan site geneli şerit, yol hedefli değildir (target_paths boş). */
export function isSitewideTopbar(popup: PublicPopup): boolean {
  return popup.type === "topbar" && !(popup.target_paths?.length);
}
